import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ChevronLeft, Gem, ShieldCheck, Star } from 'lucide-react';
import { ARTISTS } from '../data/artists';

function ArtistBooking() {
  const { artistId } = useParams();
  const [artist, setArtist] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const found = ARTISTS.find(a => a.slug === artistId);
    setArtist(found);
  }, [artistId]);

  if (!artist) {
    return (
      <div className="app-container" style={{ textAlign: 'center', paddingTop: '20vh' }}>
        <h2>Artist Not Found</h2>
        <Link to="/" className="btn btn-primary" style={{ marginTop: '20px' }}>
          <ChevronLeft size={16} /> Back to Roster
        </Link>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* ───── Navbar ───── */}
      <nav className="navbar scrolled">
        <div className="container navbar-inner">
          <Link to="/" className="logo">
            <Gem size={22} color="var(--accent-gold)" />
            <span>PYRAMID</span>
            <span className="gold">ENTERTAINMENT</span>
          </Link>
          <div className="nav-links">
            <Link to="/"><ChevronLeft size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }}/> Back to Main Site</Link>
          </div>
        </div>
      </nav>

      {/* ───── Dedicated Hero ───── */}
      <header className="hero" style={{ minHeight: '60vh', paddingBottom: '40px' }}>
        <div className="hero-bg" style={{ 
            backgroundImage: artist.image ? `url(${artist.image})` : `url(${process.env.PUBLIC_URL}/concert_background.png)`,
            backgroundPosition: artist.image ? 'center 20%' : 'center'
        }}></div>
        <div className="hero-overlay" style={{ background: 'linear-gradient(to bottom, rgba(5,5,5,0.85) 0%, rgba(5,5,5,0.7) 100%)' }}></div>
        <div className="container">
          <div className="hero-content" style={{ margin: '0 auto', textAlign: 'center' }}>
            {artist.image && (
              <div 
                className="animate-fade-in" 
                style={{ 
                  animationDelay: '0.05s', 
                  width: '120px', 
                  height: '120px', 
                  borderRadius: '50%',
                  margin: '0 auto 20px', 
                  backgroundImage: `url(${artist.image})`, 
                  backgroundSize: 'cover', 
                  backgroundPosition: 'center', 
                  border: '3px solid var(--accent-gold)', 
                  boxShadow: '0 8px 32px var(--accent-gold-glow)' 
                }}
              />
            )}
            <div className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <span className="hero-badge">Exclusive Booking Inquiry</span>
            </div>
            <h1 className="animate-fade-in" style={{ animationDelay: '0.2s', fontSize: '3.5rem' }}>
              Secure <span className="gold-gradient">{artist.name}</span> <br/>For Your Event
            </h1>
            <p className="hero-subtitle animate-fade-in" style={{ animationDelay: '0.3s', margin: '0 auto 2rem' }}>
              Submit a direct inquiry to check availability and pricing for {artist.name}. <br/>Our veteran agents handle negotiation, routing, and logistics.
            </p>
            <div className="hero-features animate-fade-in" style={{ animationDelay: '0.4s', justifyContent: 'center' }}>
              <span className="hero-feature"><ShieldCheck size={15} color="var(--accent-gold)" /> Fast Turnaround</span>
              <span className="hero-feature"><Star size={15} color="var(--accent-gold)" /> Direct Routing</span>
              <span className="hero-feature"><Gem size={15} color="var(--accent-gold)" /> 30+ Years Experience</span>
            </div>
          </div>
        </div>
      </header>

      {/* ───── Dedicated Booking Form ───── */}
      <section className="booking-section" style={{ paddingTop: '20px', paddingBottom: '100px' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <div className="glass-panel booking-form">
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px', borderBottom: '1px solid var(--surface-border)', paddingBottom: '20px' }}>
              <div style={{ padding: '12px', background: 'rgba(212, 175, 55, 0.1)', borderRadius: '12px' }}>
                <Calendar color="var(--accent-gold)" size={28} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.4rem' }}>Check Availability</h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: 'var(--accent-gold)' }}>Artist: {artist.name} • Genre: {artist.genre}</p>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Full Name</label>
                <input type="text" placeholder="Your name" autoFocus/>
              </div>
              <div className="form-group">
                <label>Company / Organization</label>
                <input type="text" placeholder="Company name" />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input type="email" placeholder="you@company.com" />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" placeholder="(555) 000-0000" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Event Date <span style={{ color:'var(--text-muted)', fontSize:'0.7rem'}}>(Approximate is OK)</span></label>
                <input type="date" />
              </div>
              <div className="form-group">
                <label>Event Type</label>
                <select defaultValue="">
                  <option value="" disabled>Select type</option>
                  <option>Concert / Festival</option>
                  <option>Corporate Event</option>
                  <option>Private Party / Gala</option>
                  <option>Casino</option>
                  <option>Fair / County Event</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Event Location / City</label>
                <input type="text" placeholder="e.g. Las Vegas, NV" />
              </div>
              <div className="form-group">
                <label>Estimated Offer Range</label>
                <select defaultValue="">
                  <option value="" disabled>Select range</option>
                  <option>$25k - $50k</option>
                  <option>$50k - $100k</option>
                  <option>$100k - $250k</option>
                  <option>$250k - $500k</option>
                  <option>$500k - $1M+</option>
                  <option>Not sure yet</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Additional Details</label>
              <textarea placeholder="Venue capacity, specific requests, schedule..." style={{ minHeight: '100px' }}/>
            </div>

            <button className="btn btn-primary" style={{ width: '100%', padding: '16px', fontSize: '1.1rem', marginTop: '10px' }}>
              Submit Request for {artist.name}
            </button>
            <p className="form-note">A senior agent will review your inquiry and contact you within 24 hours.</p>
          </div>
        </div>
      </section>
      
      {/* ───── Footer ───── */}
      <footer className="site-footer">
        <div className="container" style={{ textAlign: 'center' }}>
          <div className="footer-logo" style={{ justifyContent: 'center', marginBottom: '10px' }}>
            <Gem size={18} color="var(--accent-gold)" />
            <span>PYRAMID</span>
            <span className="gold">ENTERTAINMENT</span>
          </div>
          <p style={{ fontSize: '0.85rem' }}>© 2026 Pyramid Entertainment Group. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default ArtistBooking;
